import React from "react";
import { AlertCircle, Sparkles } from "lucide-react";
import { Card, CardContent, StatusBadge } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { AttributeReviewContextHeaderProps } from "./AttributeReviewContextHeader.types";

export const AttributeReviewContextHeader: React.FC<
  AttributeReviewContextHeaderProps
> = ({
  context,
  needDecisions,
  onlyOpen,
  onToggleOnlyOpen,
  lowConfidence,
  activeSummaryFilter,
  onSummaryFilterChange,
}) => {
  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              SKU ID
            </p>
            <p
              title={context.skuId}
              className="max-w-[20rem] truncate text-sm font-mono font-medium text-slate-900 dark:text-slate-100"
            >
              {context.skuId}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Mapping Run
            </p>
            <p
              title={context.mappingRunId}
              className="max-w-[20rem] truncate text-sm font-mono font-medium text-slate-900 dark:text-slate-100"
            >
              {context.mappingRunId}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Model Version
            </p>
            <StatusBadge status="ACTIVE" showDot={false} className="mt-1">
              <Sparkles className="w-3 h-3 mr-1" />
              {context.modelVersion}
            </StatusBadge>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Mapping
            </p>
            <p className="whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
              {context.sourceMarketplace} → {context.targetMarketplace}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onSummaryFilterChange("NEEDS_REVIEW");
              onToggleOnlyOpen();
            }}
            className={cn(
              "ml-auto inline-flex shrink-0 items-center whitespace-nowrap rounded-full transition-shadow",
              "focus:outline-none focus:ring-2 focus:ring-amber-500/30",
              activeSummaryFilter === "NEEDS_REVIEW" &&
                "ring-2 ring-amber-500/30",
            )}
            aria-pressed={onlyOpen}
            data-testid="filter-needs-review"
          >
            <StatusBadge
              status="WARNING"
              className="cursor-pointer whitespace-nowrap text-sm"
            >
              <AlertCircle className="w-3.5 h-3.5 mr-1" />
              {onlyOpen
                ? `Showing ${needDecisions} not saved · show all`
                : `${needDecisions} not saved`}
            </StatusBadge>
          </button>
          {lowConfidence > 0 && (
            <button
              type="button"
              onClick={() => onSummaryFilterChange("LOW_CONFIDENCE")}
              className={cn(
                "inline-flex shrink-0 items-center whitespace-nowrap rounded-full transition-shadow",
                "focus:outline-none focus:ring-2 focus:ring-rose-500/30",
                activeSummaryFilter === "LOW_CONFIDENCE" &&
                  "ring-2 ring-rose-500/30",
              )}
              aria-pressed={activeSummaryFilter === "LOW_CONFIDENCE"}
              data-testid="filter-low-confidence"
            >
              <StatusBadge status="FAILED" className="text-sm cursor-pointer">
                <AlertCircle className="w-3.5 h-3.5 mr-1" />
                {lowConfidence} Low Confidence
              </StatusBadge>
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
